/**
 * A leaderboard row's expansion machinery — the measured-accordion contract (expand/collapse,
 * the settle-to-`none` max-height clear, the stillborn-transition watchdog) plus the fetch that
 * fills the slot (stats-v2 panel redesign design § Rendering & derivation): {@code GET
 * /stats/panel?mode=<mode>&entry=<entryKey>&window=<window>[&month=<YYYY-MM>]} into `detail` on
 * first expand, plus the board's own memory of what it has already fetched and already mounted
 * (`boards` below, keyed `${gameMode}.${window}.${month}.${entryKey}` — mode-scoped since
 * leaderboard-dedicated-page-design and window-scoped since leaderboard-expansion-window-records,
 * which is also what makes the panel follow the board's active window rather than always reading
 * career-wide). That memory is what keeps a chip click cheap: the board is rebuilt from scratch on
 * every sort/window/mode click and the viewer's row re-opens on every rebuild, so without it each
 * click re-parsed the same fragment, rebuilt both band matrices and re-ran the whole `enhance` pass
 * over a panel that was already mounted and enhanced in the DOM being thrown away
 * (leaderboard-start-screen-click-cost).
 * Split out of `leaderboard-render.js` (leaderboard-row-stats-band-matrix design § Structure & SRP)
 * once the head-wrapper restructure was about to grow four already-yellow functions in an
 * already-yellow module — this is the coherent second responsibility: row/header/toggle wiring
 * stays in `leaderboard-render.js`, the expansion's own machinery lives here. The file name matches
 * the existing `leaderboard.expansion.test.js` (not `leaderboard-detail.js` — that name would
 * resurrect the retired `leaderboard-detail-body.js` ghost the docs still occasionally carry).
 *
 * The stats-band matrix mount (leaderboard-row-stats-band-matrix design § B, extended by the
 * leaderboard-matrix-narrow-shape brief's Decision A) rides the same fetch: `mountBandMatrix`
 * reads the fragment's `.sp-band-data` span (present only for the leaderboard-expansion case —
 * `StatsPanelViewBuilder`'s `leaderboardRow` gate, `entry != null`), parses its JSON snapshot, and
 * builds BOTH layouts (`../stats-band-matrix.js#buildMatrix`, `single` and `wrapped`) off that one
 * parsed snapshot, inserting both directly after the panel's KPI strip — always with
 * `previous === current` (the leaderboard is a rest surface, no tick, no amber — design § Out of
 * scope). Which one paints is decided declaratively by `@container stats-panel (max-width: 840px)`
 * (skat.css § Leaderboard), not by JS — chosen over measuring at mount (stale across a
 * portrait↔landscape rotate) and over a `ResizeObserver` (machinery this rest surface deliberately
 * carries none of). The accepted cost: one built-but-never-painted DOM subtree per expanded row.
 */
import { fetchFragment, mountFragment } from "../fragment-loader.js";
import { enhance } from "../stats-panel-enhance.js";
import { buildMatrix } from "../stats-band-matrix.js";

// One board's memory, keyed by its container element: `fragments` holds each view's fetched panel
// HTML promise, `panels` the viewer row's mounted detail nodes, registered at mount with `enhance`
// possibly still in flight — adoption never re-runs it; the in-flight pass finishes on the moved
// node. Both are keyed `${gameMode}.${window}.${month}.${entryKey}` and both live exactly as long
// as the board does, which is why they are one record rather than two WeakMaps. Every chip click
// rebuilds the leaderboard's whole DOM (leaderboard.js#renderLeaderboardInto), so a per-element
// "already fetched" flag would refetch on every one; keying by the full request tuple survives
// that rebuild instead — and keeps a mode OR window switch from serving another view's cached
// fragment, the same aliasing class the mode component closed (see the file header).
//
// Per board rather than module-wide (the `leaderboard.js#requestSeq` precedent, js/CLAUDE.md
// § Module-level state exceptions), for two reasons. Two boards can be live at once — a menu-opened
// overlay over a still-mounted start-screen board — and this memory holds mounted DOM, which a
// shared store would let one board steal from the other. And a page-lifetime cache had nothing to
// invalidate it: `game.js#renderGameStartScreen` returns from a game with a freshly built start
// screen in the same page, so the viewer's own row was served its pre-round panel until a reload.
// A board's memory dying with its container makes that fresh screen a fresh board, at the cost of
// the overlay re-fetching its viewer panel per open (a new content div each time).
//
// `panels` holds VIEWER rows only — the one row that re-opens on every board rebuild without any
// user action, which is what made a chip click re-mount a panel that was already on screen. Its key
// space is bounded by construction: modes (<=2) x windows (<=3) x this board's month x the single
// viewer entry, so at most six panels per board and no eviction rule to get wrong. Other rows are
// not retained — their key space is the whole board, and they re-mount only when the visitor
// expands them again, which is user action rather than per-click churn.
const boards = new WeakMap();

function boardMemory(container) {
  let memory = boards.get(container);
  if (memory == null) {
    memory = { fragments: new Map(), panels: new Map() };
    boards.set(container, memory);
  }
  return memory;
}

function panelKey(settings, entryKey) {
  return `${settings.gameMode}.${settings.window}.${settings.month}.${entryKey}`;
}

/** Inserts BOTH stats-band layouts (single, wrapped) directly after the panel's KPI strip when the
 *  fragment carries the `.sp-band-data` span — a no-op (panel untouched) when absent, which on the
 *  live path means a RAMSCH board (the builder-owned gate). The entry-less `leaderboardRow=false`
 *  shape the builder still serves has had no in-app caller since result-stats-button-opens-games-
 *  overlay retargeted the result screen's Stats tile at `/games`. The span is JSON.parse'd and
 *  removed either way, never rendering as a stray hidden element.
 *
 *  Since stats-panel-unification both layouts land INSIDE `.stats-panel-v2`, after the strip, so
 *  the panel's own flex column spaces every block uniformly and the matrix resolves its `cqw` unit
 *  against the same `stats-panel` container its shape-switch query asks (skat.css § Leaderboard) —
 *  where the old `detail.prepend` put them a box up, as siblings of the panel. No fallback for a
 *  strip-less fragment: a board's fetched fragments die with it, so new JS never meets an old
 *  fragment, and the one transitional mismatch (a page loaded before a deploy fetching a new
 *  fragment into old JS) renders matrix-then-strip until reload — accepted, not guarded.
 *
 *  `previous === current` for both builds — the whole static story (design § B): by construction
 *  no cell differs, so each matrix renders its rest state with zeros dimmed and nothing amber;
 *  `rolls` is discarded, there is no tick here. Only one of the two ever paints — skat.css's
 *  container query picks which via `display: none` on the other. */
function mountBandMatrix(detail) {
  const span = detail.querySelector(".sp-band-data");
  if (span == null) return;
  const snapshot = JSON.parse(span.dataset.band);
  span.remove();
  const single = buildMatrix(snapshot, snapshot, { layout: "single" });
  const wrapped = buildMatrix(snapshot, snapshot, { layout: "wrapped" });
  detail.querySelector(".stats-kpi-strip").after(single.el, wrapped.el);
}

/** This view's panel fragment, fetched on the first ask and held in the board's own memory (see
 *  `boards` above) for every later one. A failure evicts the entry so the next ask retries, and it
 *  is handled here rather than only at the consumer: `prefetchPanel` starts a request before any
 *  row exists to expand, and that row may never arrive (a superseded board response, a board with
 *  no viewer row), which would leave a rejection nobody ever handles.
 *
 *  `settings.window` is always known here: `leaderboard.js#renderLeaderboardInto` folds the
 *  effective window into `settings` before any row exists to click (design § 5). `settings.month`
 *  rides along only on a hall-of-fame board, where the server requires the pair. */
function fragmentFor(settings, entryKey, container) {
  const fragments = boardMemory(container).fragments;
  const cacheKey = panelKey(settings, entryKey);
  let promise = fragments.get(cacheKey);
  if (promise == null) {
    const month =
      settings.month == null
        ? ""
        : `&month=${encodeURIComponent(settings.month)}`;
    promise = fetchFragment(
      `/stats/panel?mode=${encodeURIComponent(settings.gameMode)}&entry=${encodeURIComponent(entryKey)}&window=${encodeURIComponent(settings.window)}${month}`,
    );
    promise.catch(() => fragments.delete(cacheKey));
    fragments.set(cacheKey, promise);
  }
  return promise;
}

/** Starts (or joins) this view's panel fetch without mounting anything — `leaderboard.js` fires it
 *  alongside the board's own fetch so the two round trips overlap rather than running in series
 *  (leaderboard-start-screen-click-cost § 2). Nothing beyond a Map read once this board holds the
 *  view's fragment, which includes every case where its panel is retained mounted; the row's later
 *  expand reads the same cache, so it either shares this in-flight promise or never asks at all. */
export function prefetchPanel(settings, entryKey, container) {
  fragmentFor(settings, entryKey, container);
}

/** Fetches this row's stats-v2 panel fragment (`fragmentFor` above), mounts it into `detail`,
 *  inserts the stats-band matrix when the fragment carries one, then enhances it. Never throws — a
 *  failed fetch just leaves the detail empty and warns, mirroring the other fragment consumers'
 *  render-error posture. Returns the settled (always-fulfilled) promise so `expandDetail` below can
 *  re-measure once the mount — success or failure — actually lands.
 *
 *  A `detail` already holding this very view (`_panelKey`) resolves without touching the DOM at
 *  all: `mountFragment` wipes the slot, so re-mounting would re-parse the HTML, rebuild both band
 *  matrices and re-run `enhance` (icons, bars, donut, net chart, a fresh ResizeObserver) over
 *  content already on screen. That covers a manual collapse + re-expand of any row, and every
 *  expand of a viewer panel this board retained across a rebuild (`adoptRetainedPanel`). */
function loadDetailFragment(detail, row, settings, container) {
  const cacheKey = panelKey(settings, row.entryKey);
  if (detail._panelKey === cacheKey) return Promise.resolve();
  return fragmentFor(settings, row.entryKey, container)
    .then((html) => {
      mountFragment(detail, html);
      mountBandMatrix(detail);
      // The expando marks which view this slot now holds — the file's own `_settleListener` /
      // `_cancelWatchdog` precedent. The viewer's row additionally hands the mounted node to its
      // board, which is what the next rebuild adopts instead of mounting a second copy.
      detail._panelKey = cacheKey;
      // Only a slot the board is still showing. A window switch mid-fetch discards the row that
      // started this one, and `enhance` below bakes layout reads that come out wrong on a detached
      // node — `stats-panel-enhance.js` picks its bar size off `panel.clientWidth` (0) and its
      // `blockScale` off an empty computed style — then never re-evaluates them. Retaining such a
      // node would put a compact-baked panel on screen the next time that view came back.
      // `contains` rather than `isConnected`: the start screen's first render legitimately fills a
      // section it only appends afterwards (game-start-active-runs.js#buildLeaderboardSection),
      // and that panel is this board's.
      if (row.isViewer && container.contains(detail))
        boardMemory(container).panels.set(cacheKey, detail);
      // Returned, not fire-and-forget: enhance mounts the charts behind an on-demand module load,
      // so the fragment only reaches its final height once this settles — and expandDetail's
      // max-height measurement rides this very chain.
      return enhance(detail);
    })
    .catch((e) => {
      // Give the key back. A slot whose mount or enhance FAILED holds a half-built panel nothing
      // would ever repair — distinct from the ordinary in-flight case above, which finishes on its
      // own — so the next expand re-mounts from the board's cached HTML and retries the charts,
      // which is the retry the page-lifetime cache used to buy by evicting the HTML outright.
      // Only this detail's own registration is dropped: a rebuild racing the fetch leaves the live
      // row's node under the same key, and a stale chain's failure must not take it down. Evicting
      // a failed *fetch* stays `fragmentFor`'s — the entry's owner, and the only place that sees a
      // failure nobody expanded for.
      //
      // Accepted residual (review round 3, 2026-09-05), the sibling of the double-continuation one
      // `expandDetail` already names: a collapse + re-expand while the first fetch is still in
      // flight puts two chains on ONE detail, and identity is all this guard discriminates by. If
      // the superseded chain's `enhance` rejects while the later chain's succeeds, this clears a
      // key and registration that were valid — costing one re-mount on the next rebuild, which the
      // still-cached HTML makes cheap and which repairs itself. Closing it needs a per-chain token,
      // the third bookkeeping mechanism this file declines for the same race.
      detail._panelKey = null;
      const panels = boardMemory(container).panels;
      if (panels.get(cacheKey) === detail) panels.delete(cacheKey);
      console.warn(
        "[leaderboard-expansion] stats fragment fetch failed:",
        e.message,
      );
    });
}

/** Sets `detail`'s inline max-height from its real content height — the measured-accordion
 *  contract skat.css's `.leaderboard-detail` comment describes (Bug A, stats-v2 revisit: no
 *  static cap survives the stats-v2 panel's range, from a bare KPI strip to the full matrix +
 *  Null + Ramsch). `scrollHeight` rather than the clamped `clientHeight`, since the whole point
 *  is measuring the content the current cap might be hiding. */
function setMeasuredMaxHeight(detail) {
  detail.style.maxHeight = `${detail.scrollHeight}px`;
}

/** Arms a one-shot settle listener that clears `detail`'s inline max-height cap to `none` once
 *  the transition it's currently running settles, so a later reflow (viewport resize, a
 *  re-render) isn't capped at a stale pixel value. Events are the PRIMARY settle signal: listens
 *  for `transitioncancel` alongside `transitionend` because in the live page the expand
 *  transition rarely finishes cleanly — the growing detail trips game-start-layout.js's
 *  ResizeObserver, whose fit-solve freeze (`.gs-solving`, skat.css) zeroes transition-duration
 *  mid-flight and cancels the transition — an end-only listener never fires there (found
 *  post-ship: the panel stayed pinned and re-clipped on rotate). Each new call removes its
 *  predecessor's listeners first (`detail._settleListener`) — the overtaking arm always wins,
 *  unconditionally: a pinned-value comparison alone can't tell an overtaken listener from a
 *  current one when two arms pin the SAME value, which a cached re-expand does (both the
 *  pre-fetch and the post-"fetch" arm measure the same already-mounted scrollHeight) — under
 *  equal pins the elder listener still fired, cleared the cap, and starved the younger's
 *  `onSettled` before this explicit-overtake fix (leaderboard-header-show-dont-tell review
 *  round). The pinned-value capture stays as the belt for a write that arrives without a new
 *  arm — collapse's re-pin, or any other direct style write between this call and its settle.
 *
 *  `watchStillbornTransition`, when true, additionally arms `armSettleWatchdog` below: a
 *  same-frame create+freeze race can leave a max-height write with NO transition at all, so
 *  neither event ever fires (live trace, 2026-07-19, corpus dev server: a cached re-expand's
 *  0->pinned write lands in the same rendering pass as the ResizeObserver-triggered
 *  placeStartAssembly refit, whose synchronous `.gs-solving` layout starves the transition
 *  before the browser creates it — unlike the first expand's real transition, which the freeze
 *  merely cancels mid-flight, a genuine `transitioncancel`). Without the watchdog the pin never
 *  clears on that path — the exact stale-pin re-clipping #1538 fixed, back on cached re-expand.
 *
 *  `onSettled`, when given, fires only on an actual (non-overtaken) clear — this is the
 *  "expansion genuinely settled" signal `expandDetail` threads through to `toggleRow`'s
 *  scroll-into-view (leaderboard-expand-scroll-into-view-never-fires). */
function clearMaxHeightAfterSettle(
  detail,
  li,
  onSettled,
  watchStillbornTransition,
) {
  if (detail._settleListener)
    removeSettleListener(detail, detail._settleListener);
  detail._cancelWatchdog?.(); // overtake extends to a predecessor's pending watchdog too
  detail._cancelWatchdog = null;
  const pinned = detail.style.maxHeight;
  const settle = () => {
    if (
      li.classList.contains("expanded") &&
      detail.style.maxHeight === pinned
    ) {
      detail.style.maxHeight = "none";
      onSettled?.();
    }
  };
  const onSettle = (e) => {
    if (e.target !== detail || e.propertyName !== "max-height") return;
    removeSettleListener(detail, onSettle);
    detail._settleListener = null;
    detail._cancelWatchdog?.(); // a real event fired — the watchdog's stillborn check no longer applies
    detail._cancelWatchdog = null;
    settle();
  };
  detail._settleListener = onSettle;
  detail.addEventListener("transitionend", onSettle);
  detail.addEventListener("transitioncancel", onSettle);
  if (watchStillbornTransition) armSettleWatchdog(detail, onSettle, settle);
}

// Both event types clearMaxHeightAfterSettle listens for, removed together — three call sites
// below (overtake-removal, the event listener's own self-removal, the watchdog's stillborn
// removal) all mean "this armed listener is done," never just one type on its own.
function removeSettleListener(detail, listener) {
  detail.removeEventListener("transitionend", listener);
  detail.removeEventListener("transitioncancel", listener);
}

/** The stillborn-transition fallback for `clearMaxHeightAfterSettle` above: two animation frames
 *  after arming, checks whether a live `max-height` CSSTransition actually exists
 *  (`detail.getAnimations()`, filtered on `transitionProperty`) — if one does, the event path
 *  owns settling and this stands down for good; if not, the write never got a transition at all
 *  (this call's whole reason to exist) and settles directly, using the SAME guards the event path
 *  uses (`settle`, closed over the call's own `pinned`/`onSettled`). Two frames, not one: the
 *  transition is created (or not) during style recalc, which can lag a single rAF under load.
 *  `getAnimations` is absent under jsdom (no Web Animations API) — the check is a no-op there and
 *  every existing event-path test keeps its original timing untouched. Extracted from
 *  `clearMaxHeightAfterSettle` (single caller today) because "double-rAF liveness probe" is its
 *  own coherent concept, not an inline detail of the listener-arming above it (CODING § method
 *  extraction floor). */
function armSettleWatchdog(detail, onSettle, settle) {
  let cancelled = false;
  detail._cancelWatchdog = () => {
    cancelled = true;
  };
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      if (cancelled) return;
      if (typeof detail.getAnimations !== "function") return;
      const hasLiveMaxHeightTransition = detail
        .getAnimations()
        .some((a) => a.transitionProperty === "max-height");
      if (hasLiveMaxHeightTransition) return; // a real transition IS running — its own event will fire
      removeSettleListener(detail, onSettle);
      detail._settleListener = null;
      detail._cancelWatchdog = null;
      settle();
    });
  });
}

/** The mounted panel this board already holds for `row`'s current view, or null. The
 *  caller moves the returned node into the row it is building in place of a fresh empty slot — a
 *  synchronous move inside the same task as the board swap, so the net chart's own ResizeObserver
 *  sees no width change and nothing rebuilds. An adopted panel is deliberately
 *  NOT handed to `expandDetail` below: replaying the 0->N accordion transition over content that
 *  never left the screen is exactly the per-click work this retention removes (it is also what
 *  fires the start screen's fit-solve ResizeObserver once per frame of that transition).
 *
 *  Settled as open here rather than at the call site, because the accordion contract is this
 *  module's. An adopted panel is rendered open whatever its old row left it at — settled,
 *  mid-transition, or collapsed to 0px: `max-height: none`, the resting state a settled expand
 *  leaves behind, is the whole of it, and any settle listener or watchdog
 *  still pending from the old row's last transition is dropped exactly the way
 *  `clearMaxHeightAfterSettle`'s overtake path drops a predecessor's. Left armed, one of them would
 *  later clear or re-pin this adopted node off a transition that is no longer running. */
export function adoptRetainedPanel(row, settings, container) {
  const detail = boardMemory(container).panels.get(
    panelKey(settings, row.entryKey),
  );
  if (detail == null) return null;
  if (detail._settleListener)
    removeSettleListener(detail, detail._settleListener);
  detail._settleListener = null;
  detail._cancelWatchdog?.();
  detail._cancelWatchdog = null;
  detail.style.maxHeight = "none";
  return detail;
}

/** Expand path: measures + arms the settle-to-`none` clear immediately (whatever `detail` already
 *  holds — content from a prior expand, or nothing on a never-before-expanded row), then fetches
 *  the panel fragment and repeats both once it mounts, since that mount is asynchronous and the
 *  very first expand starts from an empty `detail`. A slot already holding this view mounts nothing
 *  (`loadDetailFragment`'s `_panelKey` check) and the second measure lands on the same content.
 *
 *  `onSettled`, when given, fires once — when the expansion has genuinely settled at full mounted
 *  height, not merely when the *first* max-height transition ends or cancels (leaderboard-expand-
 *  scroll-into-view-never-fires: on the live page that first transition almost always cancels
 *  while `detail` is still near-empty — the panel fragment mounts asynchronously — so firing on it
 *  would scroll to the very "sliver" the 2026-05-17 scroll-into-view affordance exists to avoid).
 *  This function's *first* `clearMaxHeightAfterSettle` call below (the pre-fetch arm) never
 *  receives `onSettled` for exactly that reason. It fires from whichever post-fetch arm actually
 *  lands: the post-mount re-pin's own settle (a real transition, listened for below), or — when
 *  the empty-slot transition already settled before the fetch landed (`maxHeight === "none"`) —
 *  the next animation frame, since content just mounted at full height with no further max-height
 *  transition left to hook. */
export function expandDetail(detail, li, row, settings, container, onSettled) {
  setMeasuredMaxHeight(detail);
  clearMaxHeightAfterSettle(detail, li); // pre-fetch arm: not mounted yet, never notifies
  loadDetailFragment(detail, row, settings, container).then(() => {
    // A board rebuild adopted this panel into a fresh row while the fetch was still in flight:
    // that row owns the slot now and `adoptRetainedPanel` already settled it open, so every write
    // below would land on another row's node. The `.expanded` check underneath cannot catch this —
    // `li` is the discarded row, which keeps the class it was thrown away with, so it waves the
    // stale continuation through and re-pins a panel the visitor may have since collapsed.
    if (detail.parentElement !== li) return;
    if (!li.classList.contains("expanded")) return; // collapsed again before the fetch landed
    // Settle already uncapped to `none` (the empty-slot transition finished before the fetch):
    // leave it. `none -> length` never transitions, so a re-pin here would have no
    // transitionend to clear it and the pixel cap would silently go stale on the next reflow
    // (rotate/resize) — the exact clipping this contract exists to prevent. Uncapped, the
    // mounted content just shows at full height, which is the same no-animation result a
    // re-pin would produce.
    if (detail.style.maxHeight === "none") {
      // Accepted residual (codex review, leaderboard-header-show-dont-tell): a slow fetch +
      // collapse + re-expand + early-settle race can land two pending continuations here at
      // once (the stale collapsed-then-re-expanded call and the fresh one), each scheduling its
      // own onSettled via rAF — a duplicate same-target scroll, one frame apart. Harmless (same
      // `li`, and `toggleRow`'s onSettled re-checks `.expanded` at fire time) and narrow enough
      // that a third bookkeeping mechanism to de-dupe it isn't worth it.
      if (onSettled) requestAnimationFrame(onSettled);
      return;
    }
    setMeasuredMaxHeight(detail);
    // The watchdog rides only this post-fetch arm (`true`) — never the pre-fetch arm above. On a
    // never-before-expanded row the pre-fetch arm's own write starts the FIRST expand's real,
    // legitimately-animating transition; a watchdog there would fire two frames in regardless of
    // whether that transition is still genuinely running and clear the cap early, cutting the
    // animation short (worse: via the `maxHeight === "none"` branch above, it would then also
    // misread the still-animating detail as fully settled). This later arm is the one that
    // actually needs the watchdog — a cached re-expand's write is where the transition can go
    // stillborn (see the doc comment above).
    clearMaxHeightAfterSettle(detail, li, onSettled, true);
  });
}

/** Collapse path: re-pins the current pixel height first — forcing a reflow via the bare
 *  `offsetHeight` read before the second write, so the browser can't coalesce the two into one
 *  recalculation — so the base rule's `max-height: 0` has a numeric value to transition FROM.
 *  `none -> 0` doesn't animate (only two `<length>`s interpolate), and by the time a row
 *  collapses the settle above may already have cleared the inline cap to `none`. */
export function collapseDetail(detail) {
  setMeasuredMaxHeight(detail); // pin the current content height, same measure as the expand path
  detail.offsetHeight; // force layout: commit the pinned height before the drop to 0
  detail.style.maxHeight = "0px";
}
